/**
 * ルームコードの形式。
 *
 * 生成はサーバの責務（暗号論的乱数を使うため `shared` には置けない）だが、形式の判定は
 * サーバとクライアントの両方が必要とする。オーソライザは無認可の接続を弾くために使い、
 * クライアントは QR から読み取った文字列を検証するために使う。2箇所に別々の定義を持つと、
 * 片方だけ直したときに「読み取れるが接続できないコード」が生まれる。
 */

/** 紛らわしい文字（0 O 1 I L）を除いた31文字。 */
export const ROOM_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

export const ROOM_CODE_LENGTH = 6;

export function isRoomCodeShape(value: string): boolean {
  if (value.length !== ROOM_CODE_LENGTH) return false;
  return [...value].every((c) => ROOM_CODE_ALPHABET.includes(c));
}

/**
 * QR コードから読み取った文字列をルームコードに解釈する（要件1.11）。
 *
 * 中央ディスプレイの QR は参加 URL（`https://…/?room=CODE`）を載せているが、他の経路で
 * コードだけが書かれた QR を読むこともありうるため、どちらも受ける。
 *
 * 解釈できない文字列には null を返す。読み取りは利用者の確認を挟まずそのまま遷移させる
 * ため、ここを緩めると関係のない QR を読んだだけで存在しない卓へ飛ばされる。
 *
 * URL のホストは見ない。別のデプロイ先を指す QR でも、載っているコードは自分の接続先で
 * そのまま使える。ホストを縛ると、独自ドメインと CloudFront ドメインのどちらで配信されて
 * いるかに依存する検証になる。
 */
export function roomCodeFromScannedText(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;

  const fromUrl = roomParamOf(trimmed);
  const candidate = (fromUrl ?? trimmed).trim().toUpperCase();
  return isRoomCodeShape(candidate) ? candidate : null;
}

/**
 * クエリ文字列から `room` を取り出す。見つからなければ null。
 *
 * `URL` を使わずに自分で切る。`shared` は実行環境に依存しない純関数だけを置く場所で、
 * DOM も Node も型として持たない。ここで `URL` を使うと lib を足すことになり、その先で
 * `fetch` や `localStorage` が書けてしまう。
 */
function roomParamOf(text: string): string | null {
  const start = text.indexOf('?');
  if (start === -1) return null;
  // フラグメントはクエリの後ろに来る。
  const query = text.slice(start + 1).split('#')[0] ?? '';
  for (const pair of query.split('&')) {
    const eq = pair.indexOf('=');
    if (eq === -1) continue;
    if (pair.slice(0, eq) !== 'room') continue;
    try {
      return decodeURIComponent(pair.slice(eq + 1));
    } catch {
      // 壊れたパーセントエンコーディング。解釈できないものとして扱う。
      return null;
    }
  }
  return null;
}
